import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    IconButton,
    InputAdornment
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Lock as LockIcon
} from '@mui/icons-material';
import './PasswordSection.css';
import userService from '../../services/userService';

const PasswordSection = () => {
    const { t } = useTranslation('profile');
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handlePasswordChange = (field) => (event) => {
        setPasswords(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        setError('');
        setSuccess('');
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validatePasswords = () => {
        if (!passwords.current || !passwords.new || !passwords.confirm) {
            setError(t('password.allRequired'));
            return false;
        }
        if (passwords.new !== passwords.confirm) {
            setError(t('password.mismatch'));
            return false;
        }
        if (passwords.new.length < 8) {
            setError(t('password.tooShort'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validatePasswords()) {
            return;
        }

        try {
            await userService.updatePassword(passwords.current, passwords.new);
            setSuccess(t('password.success'));
            setPasswords({ current: '', new: '', confirm: '' });
            setShowPasswords({ current: false, new: false, confirm: false });
        } catch (err) {
            setError(err.response?.data?.message || t('password.failed'));
        }
    };

    return (
        <Box className="password-section">
            <Paper elevation={1} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LockIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="h2">
                        {t('password.title')}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label={t('password.current')}
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwords.current}
                        onChange={handlePasswordChange('current')}
                        margin="normal"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => togglePasswordVisibility('current')}
                                        edge="end"
                                    >
                                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('password.new')}
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwords.new}
                        onChange={handlePasswordChange('new')}
                        margin="normal"
                        required
                        helperText={t('password.helper')}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => togglePasswordVisibility('new')}
                                        edge="end"
                                    >
                                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        fullWidth
                        label={t('password.confirm')}
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwords.confirm}
                        onChange={handlePasswordChange('confirm')}
                        margin="normal"
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => togglePasswordVisibility('confirm')}
                                        edge="end"
                                    >
                                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 3 }}
                    >
                        {t('password.submit')}
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default PasswordSection;
